"use client";

import { cn } from "@repo/ui";
import { useCursorPortalRoot } from "@shared/hooks/use-cursor-portal-root";
import { useAppRoutePending } from "@shared/lib/app-route-pending-context";
import {
	Ban,
	CircleHelp,
	Hand,
	HandGrab,
	Loader2,
	MousePointerClick,
	MoveDiagonal,
	MoveHorizontal,
	MoveVertical,
	ZoomIn,
	ZoomOut,
} from "lucide-react";
import { motion, useSpring } from "motion/react";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface Position {
	x: number;
	y: number;
}

type CursorIntent =
	| "none"
	| "pointer"
	| "grab"
	| "not-allowed"
	| "info"
	| "zoom-in"
	| "zoom-out"
	| "resize-nwse"
	| "resize-nesw"
	| "resize-ns"
	| "resize-ew";

/**
 * Optional override when CSS `cursor` is ambiguous (e.g. `!cursor-*` fighting
 * `html.cursor-none-override`). Values: `none` | `pointer` | `grab` | `not-allowed` | `info` | `zoom-in` | `zoom-out` | `resize-nwse` | `resize-nesw` | `resize-ns` | `resize-ew`.
 */
export const SQ_CURSOR_INTENT_DATA_ATTRIBUTE = "data-sq-cursor-intent";

function readDataCursorIntent(el: Element): CursorIntent | null {
	if (!(el instanceof HTMLElement)) {
		return null;
	}
	const raw = el.getAttribute(SQ_CURSOR_INTENT_DATA_ATTRIBUTE);
	if (
		raw === "none" ||
		raw === "pointer" ||
		raw === "grab" ||
		raw === "not-allowed" ||
		raw === "info" ||
		raw === "zoom-in" ||
		raw === "zoom-out" ||
		raw === "resize-nwse" ||
		raw === "resize-nesw" ||
		raw === "resize-ns" ||
		raw === "resize-ew"
	) {
		return raw;
	}
	return null;
}

function isInteractiveElement(el: Element): boolean {
	return el.matches(
		"button, a, input, select, textarea, summary, [role='button'], [role='link'], [tabindex]",
	);
}

function isActuallyDisabled(el: Element): boolean {
	return el.matches(
		":disabled, [disabled], [aria-disabled='true'], [data-disabled='true']",
	);
}

function hasNotAllowedCursor(el: Element): boolean {
	const className = el.getAttribute("class") ?? "";
	if (className.length > 0) {
		const classTokens = className
			.split(/\s+/)
			.filter((token) => token.length > 0 && !token.includes("["));
		// Only treat unprefixed utilities as explicit intent.
		// Ignore variant tokens like `disabled:!cursor-not-allowed`.
		if (
			classTokens.some(
				(token) =>
					token === "cursor-not-allowed" ||
					token === "!cursor-not-allowed",
			)
		) {
			return true;
		}
		const disabled = isActuallyDisabled(el);
		// Support utility tokens like `disabled:cursor-not-allowed` only when this
		// element is actually disabled; this avoids enabled false positives.
		if (
			disabled &&
			classTokens.some(
				(token) =>
					token === "disabled:cursor-not-allowed" ||
					token === "disabled:!cursor-not-allowed",
			)
		) {
			return true;
		}
	}
	if (el instanceof HTMLElement) {
		if (el.style.cursor === "not-allowed") {
			return true;
		}
	}
	return window.getComputedStyle(el).cursor === "not-allowed";
}

function hasInfoCursor(el: Element): boolean {
	if (isActuallyDisabled(el)) {
		return false;
	}
	const className = el.getAttribute("class") ?? "";
	if (className.length > 0) {
		const classTokens = className
			.split(/\s+/)
			.filter((token) => token.length > 0 && !token.includes("["));
		if (
			classTokens.some(
				(token) => token === "cursor-help" || token === "!cursor-help",
			)
		) {
			return true;
		}
		const disabled = isActuallyDisabled(el);
		if (
			!disabled &&
			classTokens.some(
				(token) =>
					token === "enabled:cursor-help" ||
					token === "enabled:!cursor-help",
			)
		) {
			return true;
		}
	}
	if (el instanceof HTMLElement) {
		if (el.style.cursor === "help") {
			return true;
		}
	}
	return window.getComputedStyle(el).cursor === "help";
}

function hasPointerCursor(el: Element): boolean {
	if (isActuallyDisabled(el)) {
		return false;
	}
	const className = el.getAttribute("class") ?? "";
	if (className.length > 0) {
		const classTokens = className
			.split(/\s+/)
			.filter((token) => token.length > 0 && !token.includes("["));
		if (
			classTokens.some(
				(token) =>
					token === "cursor-pointer" || token === "!cursor-pointer",
			)
		) {
			return true;
		}
		const disabled = isActuallyDisabled(el);
		// Match state variants like `enabled:cursor-pointer` only when enabled.
		if (
			!disabled &&
			classTokens.some(
				(token) =>
					token === "enabled:cursor-pointer" ||
					token === "enabled:!cursor-pointer",
			)
		) {
			return true;
		}
	}
	if (el instanceof HTMLElement) {
		if (el.style.cursor === "pointer") {
			return true;
		}
	}
	return window.getComputedStyle(el).cursor === "pointer";
}

function hasZoomInCursor(el: Element): boolean {
	if (isActuallyDisabled(el)) {
		return false;
	}
	const className = el.getAttribute("class") ?? "";
	if (className.length > 0) {
		const classTokens = className
			.split(/\s+/)
			.filter((token) => token.length > 0 && !token.includes("["));
		if (
			classTokens.some(
				(token) =>
					token === "cursor-zoom-in" || token === "!cursor-zoom-in",
			)
		) {
			return true;
		}
		const disabled = isActuallyDisabled(el);
		if (
			!disabled &&
			classTokens.some(
				(token) =>
					token === "enabled:cursor-zoom-in" ||
					token === "enabled:!cursor-zoom-in",
			)
		) {
			return true;
		}
	}
	if (el instanceof HTMLElement) {
		if (el.style.cursor === "zoom-in") {
			return true;
		}
	}
	// Under `cursor-none-override`, computed `cursor` is `none`; yarl uses English default labels.
	if (
		typeof el.matches === "function" &&
		el.matches('.yarl__toolbar .yarl__button[aria-label="Zoom in"]')
	) {
		return true;
	}
	return window.getComputedStyle(el).cursor === "zoom-in";
}

function hasZoomOutCursor(el: Element): boolean {
	if (isActuallyDisabled(el)) {
		return false;
	}
	const className = el.getAttribute("class") ?? "";
	if (className.length > 0) {
		const classTokens = className
			.split(/\s+/)
			.filter((token) => token.length > 0 && !token.includes("["));
		if (
			classTokens.some(
				(token) =>
					token === "cursor-zoom-out" || token === "!cursor-zoom-out",
			)
		) {
			return true;
		}
		const disabled = isActuallyDisabled(el);
		if (
			!disabled &&
			classTokens.some(
				(token) =>
					token === "enabled:cursor-zoom-out" ||
					token === "enabled:!cursor-zoom-out",
			)
		) {
			return true;
		}
	}
	if (el instanceof HTMLElement) {
		if (el.style.cursor === "zoom-out") {
			return true;
		}
	}
	if (
		typeof el.matches === "function" &&
		el.matches('.yarl__toolbar .yarl__button[aria-label="Zoom out"]')
	) {
		return true;
	}
	return window.getComputedStyle(el).cursor === "zoom-out";
}

function hasGrabCursor(el: Element): boolean {
	if (isActuallyDisabled(el)) {
		return false;
	}
	const className = el.getAttribute("class") ?? "";
	if (className.length > 0) {
		const classTokens = className
			.split(/\s+/)
			.filter((token) => token.length > 0 && !token.includes("["));
		if (
			classTokens.some(
				(token) =>
					token === "cursor-grab" ||
					token === "!cursor-grab" ||
					token === "cursor-grabbing" ||
					token === "!cursor-grabbing",
			)
		) {
			return true;
		}
	}
	if (el instanceof HTMLElement) {
		if (el.style.cursor === "grab" || el.style.cursor === "grabbing") {
			return true;
		}
	}
	const computed = window.getComputedStyle(el).cursor;
	return computed === "grab" || computed === "grabbing";
}

const RESIZE_NWSE_CURSOR_TOKENS = new Set([
	"cursor-nwse-resize",
	"!cursor-nwse-resize",
	"cursor-se-resize",
	"!cursor-se-resize",
	"cursor-nw-resize",
	"!cursor-nw-resize",
]);

const RESIZE_NESW_CURSOR_TOKENS = new Set([
	"cursor-nesw-resize",
	"!cursor-nesw-resize",
	"cursor-ne-resize",
	"!cursor-ne-resize",
	"cursor-sw-resize",
	"!cursor-sw-resize",
]);

const RESIZE_NS_CURSOR_TOKENS = new Set([
	"cursor-row-resize",
	"!cursor-row-resize",
	"cursor-ns-resize",
	"!cursor-ns-resize",
	"cursor-n-resize",
	"!cursor-n-resize",
	"cursor-s-resize",
	"!cursor-s-resize",
]);

const RESIZE_EW_CURSOR_TOKENS = new Set([
	"cursor-col-resize",
	"!cursor-col-resize",
	"cursor-ew-resize",
	"!cursor-ew-resize",
	"cursor-e-resize",
	"!cursor-e-resize",
	"cursor-w-resize",
	"!cursor-w-resize",
]);

function hasResizeCursor(
	el: Element,
	tokens: ReadonlySet<string>,
	computedValues: string[],
): boolean {
	if (isActuallyDisabled(el)) {
		return false;
	}
	const className = el.getAttribute("class") ?? "";
	if (className.length > 0) {
		const classTokens = className
			.split(/\s+/)
			.filter((token) => token.length > 0 && !token.includes("["));
		if (classTokens.some((token) => tokens.has(token))) {
			return true;
		}
	}
	if (el instanceof HTMLElement) {
		if (computedValues.includes(el.style.cursor)) {
			return true;
		}
	}
	return computedValues.includes(window.getComputedStyle(el).cursor);
}

function hasResizeNwseCursor(el: Element): boolean {
	return hasResizeCursor(el, RESIZE_NWSE_CURSOR_TOKENS, [
		"nwse-resize",
		"se-resize",
		"nw-resize",
	]);
}

function hasResizeNeswCursor(el: Element): boolean {
	return hasResizeCursor(el, RESIZE_NESW_CURSOR_TOKENS, [
		"nesw-resize",
		"ne-resize",
		"sw-resize",
	]);
}

function hasResizeNsCursor(el: Element): boolean {
	return hasResizeCursor(el, RESIZE_NS_CURSOR_TOKENS, [
		"row-resize",
		"ns-resize",
		"n-resize",
		"s-resize",
	]);
}

function hasResizeEwCursor(el: Element): boolean {
	return hasResizeCursor(el, RESIZE_EW_CURSOR_TOKENS, [
		"col-resize",
		"ew-resize",
		"e-resize",
		"w-resize",
	]);
}

function getCursorIntentAtPoint(x: number, y: number): CursorIntent {
	const el = document.elementFromPoint(x, y);
	if (!el) {
		return "none";
	}

	// Keep detection strict: explicit cursor intent, with guarded
	// inheritance from non-interactive ancestors only.
	let node: Element | null = el;
	let hops = 0;
	let hasInteractiveBetween = false;
	while (node && hops < 12) {
		const nodeIsInteractive = isInteractiveElement(node);
		const dataIntent = readDataCursorIntent(node);
		if (dataIntent === "not-allowed") {
			return "not-allowed";
		}
		if (dataIntent === "resize-nwse") {
			return "resize-nwse";
		}
		if (dataIntent === "resize-nesw") {
			return "resize-nesw";
		}
		if (dataIntent === "resize-ns") {
			return "resize-ns";
		}
		if (dataIntent === "resize-ew") {
			return "resize-ew";
		}
		if (
			dataIntent === "pointer" &&
			nodeIsInteractive &&
			!isActuallyDisabled(node)
		) {
			return "pointer";
		}
		if (
			dataIntent === "grab" &&
			nodeIsInteractive &&
			!isActuallyDisabled(node)
		) {
			return "grab";
		}
		if (
			dataIntent === "info" &&
			nodeIsInteractive &&
			!isActuallyDisabled(node)
		) {
			return "info";
		}
		if (
			dataIntent === "zoom-in" &&
			nodeIsInteractive &&
			!isActuallyDisabled(node)
		) {
			return "zoom-in";
		}
		if (
			dataIntent === "zoom-out" &&
			nodeIsInteractive &&
			!isActuallyDisabled(node)
		) {
			return "zoom-out";
		}
		if (dataIntent === "none") {
			return "none";
		}
		// Disabled controls often resolve to `cursor: default` in the browser even
		// when utilities set not-allowed; treat as not-allowed for the custom cursor.
		if (nodeIsInteractive && isActuallyDisabled(node)) {
			return "not-allowed";
		}
		if (hasNotAllowedCursor(node)) {
			// If the not-allowed node is itself interactive, respect it directly.
			if (nodeIsInteractive) {
				return "not-allowed";
			}
			// Otherwise only inherit from this ancestor when there isn't an
			// interactive element between hovered target and this ancestor.
			return !hasInteractiveBetween ? "not-allowed" : "none";
		}
		if (hasInfoCursor(node)) {
			if (nodeIsInteractive) {
				return "info";
			}
			return !hasInteractiveBetween ? "info" : "none";
		}
		if (hasZoomInCursor(node)) {
			if (nodeIsInteractive) {
				return "zoom-in";
			}
			return !hasInteractiveBetween ? "zoom-in" : "none";
		}
		if (hasZoomOutCursor(node)) {
			if (nodeIsInteractive) {
				return "zoom-out";
			}
			return !hasInteractiveBetween ? "zoom-out" : "none";
		}
		if (hasResizeNwseCursor(node)) {
			if (nodeIsInteractive) {
				return "resize-nwse";
			}
			return !hasInteractiveBetween ? "resize-nwse" : "none";
		}
		if (hasResizeNeswCursor(node)) {
			if (nodeIsInteractive) {
				return "resize-nesw";
			}
			return !hasInteractiveBetween ? "resize-nesw" : "none";
		}
		if (hasResizeNsCursor(node)) {
			if (nodeIsInteractive) {
				return "resize-ns";
			}
			return !hasInteractiveBetween ? "resize-ns" : "none";
		}
		if (hasResizeEwCursor(node)) {
			if (nodeIsInteractive) {
				return "resize-ew";
			}
			return !hasInteractiveBetween ? "resize-ew" : "none";
		}
		if (hasPointerCursor(node)) {
			if (nodeIsInteractive) {
				return "pointer";
			}
			return !hasInteractiveBetween ? "pointer" : "none";
		}
		if (hasGrabCursor(node)) {
			if (nodeIsInteractive) {
				return "grab";
			}
			return !hasInteractiveBetween ? "grab" : "none";
		}
		if (nodeIsInteractive) {
			hasInteractiveBetween = true;
		}
		node = node.parentElement;
		hops += 1;
	}
	return "none";
}

export interface SmoothCursorProps {
	cursor?: React.ReactNode;
	springConfig?: {
		damping: number;
		stiffness: number;
		mass: number;
		restDelta: number;
	};
	/** Whether to center the cursor node on the pointer (default) or align top-left. */
	alignCenter?: boolean;
	/** Opacity of the rendered cursor (0–1). Defaults to 1. */
	opacity?: number;
	/** Whether the cursor should rotate based on movement direction. Defaults to true. */
	rotate?: boolean;
	/** X offset in pixels, relative to the actual cursor position. Defaults to 0. */
	offsetX?: number;
	/** Y offset in pixels, relative to the actual cursor position. Defaults to 0. */
	offsetY?: number;
	/**
	 * Pointer / grab / not-allowed / info / route spinner badges beside the cursor.
	 * Disable on secondary instances (portal `SmoothCursor` in `cursor-message.tsx`) that only render custom `cursor` content.
	 */
	showIntentBadges?: boolean;
}

const DefaultCursorSVG: FC = () => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={50}
			height={54}
			viewBox="0 0 50 54"
			fill="none"
			style={{ scale: 0.5 }}
			aria-hidden
			focusable={false}
		>
			<title>Cursor</title>
			<g filter="url(#filter0_d_91_7928)">
				<path
					d="M42.6817 41.1495L27.5103 6.79925C26.7269 5.02557 24.2082 5.02558 23.3927 6.79925L7.59814 41.1495C6.75833 42.9759 8.52712 44.8902 10.4125 44.1954L24.3757 39.0496C24.8829 38.8627 25.4385 38.8627 25.9422 39.0496L39.8121 44.1954C41.6849 44.8902 43.4884 42.9759 42.6817 41.1495Z"
					fill="black"
				/>
				<path
					d="M43.7146 40.6933L28.5431 6.34306C27.3556 3.65428 23.5772 3.69516 22.3668 6.32755L6.57226 40.6778C5.3134 43.4156 7.97238 46.298 10.803 45.2549L24.7662 40.109C25.0221 40.0147 25.2999 40.0156 25.5494 40.1082L39.4193 45.254C42.2261 46.2953 44.9254 43.4347 43.7146 40.6933Z"
					stroke="white"
					strokeWidth={2.25825}
				/>
			</g>
			<defs>
				<filter
					id="filter0_d_91_7928"
					x={0.602397}
					y={0.952444}
					width={49.0584}
					height={52.428}
					filterUnits="userSpaceOnUse"
					colorInterpolationFilters="sRGB"
				>
					<feFlood floodOpacity={0} result="BackgroundImageFix" />
					<feColorMatrix
						in="SourceAlpha"
						type="matrix"
						values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
						result="hardAlpha"
					/>
					<feOffset dy={2.25825} />
					<feGaussianBlur stdDeviation={2.25825} />
					<feComposite in2="hardAlpha" operator="out" />
					<feColorMatrix
						type="matrix"
						values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"
					/>
					<feBlend
						mode="normal"
						in2="BackgroundImageFix"
						result="effect1_dropShadow_91_7928"
					/>
					<feBlend
						mode="normal"
						in="SourceGraphic"
						in2="effect1_dropShadow_91_7928"
						result="shape"
					/>
				</filter>
			</defs>
		</svg>
	);
};

export function SmoothCursor({
	cursor = <DefaultCursorSVG />,
	springConfig = {
		damping: 45,
		stiffness: 400,
		mass: 1,
		restDelta: 0.001,
	},
	alignCenter = true,
	opacity = 1,
	rotate = true,
	offsetX = 0,
	offsetY = 0,
	showIntentBadges = true,
}: SmoothCursorProps) {
	const portalRoot = useCursorPortalRoot();
	const isRoutePending = useAppRoutePending();
	const [cursorIntent, setCursorIntent] = useState<CursorIntent>("none");
	const [isNotAllowedHover, setIsNotAllowedHover] = useState(false);
	const [isPointerHover, setIsPointerHover] = useState(false);
	const [isZoomInHover, setIsZoomInHover] = useState(false);
	const [isZoomOutHover, setIsZoomOutHover] = useState(false);
	const [isGrabHover, setIsGrabHover] = useState(false);
	const [isResizeNwseHover, setIsResizeNwseHover] = useState(false);
	const [isResizeNeswHover, setIsResizeNeswHover] = useState(false);
	const [isResizeNsHover, setIsResizeNsHover] = useState(false);
	const [isResizeEwHover, setIsResizeEwHover] = useState(false);
	const [isInfoHover, setIsInfoHover] = useState(false);
	const [isPointerPressed, setIsPointerPressed] = useState(false);
	const [isGrabDragActive, setIsGrabDragActive] = useState(false);
	const lastMousePos = useRef<Position>({ x: 0, y: 0 });
	const velocity = useRef<Position>({ x: 0, y: 0 });
	const lastUpdateTime = useRef(Date.now());
	const previousAngle = useRef(0);
	const accumulatedRotation = useRef(0);
	const lastIntentRef = useRef<CursorIntent>("none");
	const lastPressedRef = useRef(false);

	const applyIntent = useCallback((nextIntent: CursorIntent) => {
		if (lastIntentRef.current === nextIntent) {
			return;
		}
		lastIntentRef.current = nextIntent;
		setCursorIntent(nextIntent);
		setIsNotAllowedHover(nextIntent === "not-allowed");
		setIsPointerHover(nextIntent === "pointer");
		setIsZoomInHover(nextIntent === "zoom-in");
		setIsZoomOutHover(nextIntent === "zoom-out");
		setIsGrabHover(nextIntent === "grab");
		setIsResizeNwseHover(nextIntent === "resize-nwse");
		setIsResizeNeswHover(nextIntent === "resize-nesw");
		setIsResizeNsHover(nextIntent === "resize-ns");
		setIsResizeEwHover(nextIntent === "resize-ew");
		setIsInfoHover(nextIntent === "info");
	}, []);

	const cursorX = useSpring(0, springConfig);
	const cursorY = useSpring(0, springConfig);
	const rotation = useSpring(0, {
		...springConfig,
		damping: 60,
		stiffness: 300,
	});
	const scale = useSpring(1, {
		...springConfig,
		stiffness: 500,
		damping: 35,
	});

	useEffect(() => {
		type CursorMoveEvent = Pick<
			MouseEvent,
			"clientX" | "clientY" | "buttons"
		>;

		const updateVelocity = (currentPos: Position) => {
			const currentTime = Date.now();
			const deltaTime = currentTime - lastUpdateTime.current;

			if (deltaTime > 0) {
				velocity.current = {
					x: (currentPos.x - lastMousePos.current.x) / deltaTime,
					y: (currentPos.y - lastMousePos.current.y) / deltaTime,
				};
			}

			lastUpdateTime.current = currentTime;
			lastMousePos.current = currentPos;
		};

		const smoothMouseMove = (e: CursorMoveEvent) => {
			const currentPos = { x: e.clientX, y: e.clientY };
			updateVelocity(currentPos);

			const speed = Math.sqrt(
				velocity.current.x ** 2 + velocity.current.y ** 2,
			);

			cursorX.set(currentPos.x + offsetX);
			cursorY.set(currentPos.y + offsetY);
			const nextPressed = e.buttons > 0;
			if (lastPressedRef.current !== nextPressed) {
				lastPressedRef.current = nextPressed;
				setIsPointerPressed(nextPressed);
			}

			if (!isGrabDragActive) {
				const nextIntent = getCursorIntentAtPoint(
					currentPos.x,
					currentPos.y,
				);
				applyIntent(nextIntent);
			}

			if (speed > 0.1) {
				const currentAngle =
					Math.atan2(velocity.current.y, velocity.current.x) *
						(180 / Math.PI) +
					90;

				let angleDiff = currentAngle - previousAngle.current;
				if (angleDiff > 180) {
					angleDiff -= 360;
				}
				if (angleDiff < -180) {
					angleDiff += 360;
				}
				if (rotate) {
					accumulatedRotation.current += angleDiff;
					rotation.set(accumulatedRotation.current);
					previousAngle.current = currentAngle;
				}
				scale.set(0.95);
			} else {
				scale.set(1);
			}
		};

		let rafId = 0;
		const throttledMouseMove = (e: CursorMoveEvent) => {
			if (rafId) {
				return;
			}
			rafId = window.requestAnimationFrame(() => {
				smoothMouseMove(e);
				rafId = 0;
			});
		};

		// Capture phase: lightboxes (e.g. yet-another-react-lightbox zoom pan) call
		// `stopPropagation()` on `pointermove`, which prevents the bubble phase from
		// reaching `window` — without capture the custom cursor would freeze while zoomed.
		const capture = true;
		window.addEventListener("pointermove", throttledMouseMove, capture);
		// Native HTML5 drag & drop may stop move events; `dragover` still fires.
		window.addEventListener("dragover", throttledMouseMove, capture);
		return () => {
			window.removeEventListener(
				"pointermove",
				throttledMouseMove,
				capture,
			);
			window.removeEventListener("dragover", throttledMouseMove, capture);
			if (rafId) {
				window.cancelAnimationFrame(rafId);
			}
		};
	}, [
		applyIntent,
		cursorX,
		cursorY,
		rotation,
		scale,
		offsetX,
		offsetY,
		rotate,
		isGrabDragActive,
	]);

	useEffect(() => {
		const setPressed = (nextPressed: boolean) => {
			if (lastPressedRef.current !== nextPressed) {
				lastPressedRef.current = nextPressed;
				setIsPointerPressed(nextPressed);
			}
		};
		const onPointerDown = () => setPressed(true);
		const onPointerUp = () => {
			setPressed(false);
			setIsGrabDragActive(false);
			applyIntent(
				getCursorIntentAtPoint(
					lastMousePos.current.x,
					lastMousePos.current.y,
				),
			);
		};
		const onPointerCancel = () => {
			setPressed(false);
			setIsGrabDragActive(false);
		};
		const onBlur = () => {
			setPressed(false);
			setIsGrabDragActive(false);
		};
		const onVisibilityChange = () => {
			if (document.hidden) {
				setPressed(false);
				setIsGrabDragActive(false);
			}
		};
		const onPointerDownWithIntent = () => {
			onPointerDown();
			if (lastIntentRef.current === "grab") {
				setIsGrabDragActive(true);
			}
		};

		const capture = true;
		window.addEventListener(
			"pointerdown",
			onPointerDownWithIntent,
			capture,
		);
		window.addEventListener("pointerup", onPointerUp, capture);
		window.addEventListener("pointercancel", onPointerCancel, capture);
		window.addEventListener("blur", onBlur);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			window.removeEventListener(
				"pointerdown",
				onPointerDownWithIntent,
				capture,
			);
			window.removeEventListener("pointerup", onPointerUp, capture);
			window.removeEventListener(
				"pointercancel",
				onPointerCancel,
				capture,
			);
			window.removeEventListener("blur", onBlur);
			document.removeEventListener(
				"visibilitychange",
				onVisibilityChange,
			);
		};
	}, [applyIntent]);

	const dragLockActive = isGrabDragActive && isPointerPressed;
	const showNotAllowedBadge =
		!isRoutePending && !dragLockActive && isNotAllowedHover;
	const showInfoBadge =
		!isRoutePending &&
		!dragLockActive &&
		isInfoHover &&
		cursorIntent !== "not-allowed";
	const showPointerBadge =
		!isRoutePending &&
		!dragLockActive &&
		isPointerHover &&
		cursorIntent !== "not-allowed" &&
		cursorIntent !== "info" &&
		cursorIntent !== "zoom-in" &&
		cursorIntent !== "zoom-out" &&
		cursorIntent !== "resize-nwse" &&
		cursorIntent !== "resize-nesw" &&
		cursorIntent !== "resize-ns" &&
		cursorIntent !== "resize-ew";
	const showZoomInBadge =
		!isRoutePending &&
		!dragLockActive &&
		isZoomInHover &&
		cursorIntent !== "not-allowed";
	const showZoomOutBadge =
		!isRoutePending &&
		!dragLockActive &&
		isZoomOutHover &&
		cursorIntent !== "not-allowed";
	const showGrabBadge =
		!isRoutePending &&
		(dragLockActive || (isGrabHover && cursorIntent !== "not-allowed"));
	const showResizeNwseBadge =
		!isRoutePending &&
		!dragLockActive &&
		isResizeNwseHover &&
		cursorIntent !== "not-allowed";
	const showResizeNeswBadge =
		!isRoutePending &&
		!dragLockActive &&
		isResizeNeswHover &&
		cursorIntent !== "not-allowed";
	const showResizeNsBadge =
		!isRoutePending &&
		!dragLockActive &&
		isResizeNsHover &&
		cursorIntent !== "not-allowed";
	const showResizeEwBadge =
		!isRoutePending &&
		!dragLockActive &&
		isResizeEwHover &&
		cursorIntent !== "not-allowed";
	const showRouteSpinner = isRoutePending;
	const showBadge =
		showIntentBadges &&
		(showRouteSpinner ||
			showNotAllowedBadge ||
			showInfoBadge ||
			showZoomInBadge ||
			showZoomOutBadge ||
			showResizeNwseBadge ||
			showResizeNeswBadge ||
			showResizeNsBadge ||
			showResizeEwBadge ||
			showPointerBadge ||
			showGrabBadge);

	const fullscreenPortalActive =
		typeof document !== "undefined" &&
		portalRoot != null &&
		portalRoot !== document.body &&
		document.fullscreenElement === portalRoot;

	// yarl (yet-another-react-lightbox) portal defaults to z-index 9999 (`--yarl__portal_zindex`).
	// Stay above overlays/lightboxes while non-fullscreen; match prior max stacking in fullscreen.
	const cursorZIndex = fullscreenPortalActive ? 2_147_483_646 : 100_000;

	if (portalRoot == null) {
		return null;
	}

	return createPortal(
		<motion.div
			style={{
				position: "fixed",
				left: cursorX,
				top: cursorY,
				translateX: alignCenter ? "-50%" : "0%",
				translateY: alignCenter ? "-50%" : "0%",
				scale,
				zIndex: cursorZIndex,
				pointerEvents: "none",
				willChange: "transform",
				opacity,
			}}
			initial={{ scale: 0 }}
			animate={{ scale: 1 }}
			transition={{ type: "spring", stiffness: 400, damping: 30 }}
		>
			<div className="relative">
				<motion.div style={{ rotate: rotate ? rotation : 0 }}>
					{cursor}
				</motion.div>
				<div
					className="absolute top-2 right-2 translate-x-1/2 -translate-y-1/2"
					aria-hidden
				>
					<motion.div
						initial={false}
						animate={{
							opacity: showBadge ? 1 : 0,
							scale: showBadge ? 1 : 0,
						}}
						style={{ transformOrigin: "center center" }}
						transition={{ duration: 0.3, ease: "easeOut" }}
					>
						<div className="rounded-full bg-background/90 p-1 shadow-sm backdrop-blur">
							{showRouteSpinner ? (
								<Loader2
									aria-hidden
									className="size-3 animate-spin text-foreground/80"
								/>
							) : showNotAllowedBadge ? (
								<Ban className="size-3 text-foreground/80" />
							) : showInfoBadge ? (
								<CircleHelp className="size-3 text-foreground/80" />
							) : showZoomInBadge ? (
								<ZoomIn className="size-3 text-foreground/80" />
							) : showZoomOutBadge ? (
								<ZoomOut className="size-3 text-foreground/80" />
							) : showResizeNwseBadge || showResizeNeswBadge ? (
								<MoveDiagonal
									className={cn(
										"size-3 text-foreground/80",
										showResizeNeswBadge && "rotate-90",
									)}
								/>
							) : showResizeNsBadge ? (
								<MoveVertical className="size-3 text-foreground/80" />
							) : showResizeEwBadge ? (
								<MoveHorizontal className="size-3 text-foreground/80" />
							) : showPointerBadge ? (
								<MousePointerClick className="size-3 text-foreground/80" />
							) : showGrabBadge ? (
								isPointerPressed ? (
									<HandGrab className="size-3 text-foreground/80" />
								) : (
									<Hand className="size-3 text-foreground/80" />
								)
							) : null}
						</div>
					</motion.div>
				</div>
			</div>
		</motion.div>,
		portalRoot,
	);
}
